
import { appointmentsModule, companyModule } from '@/store/modules/store-accessor'
import dayjs from 'dayjs';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import TheLoadingIndicator from "@/components/TheLoadingIndicator.vue";
import { getOptimalTextColor, priorityIcon } from '@/helpers/data';

@Component({
    components: { TheLoadingIndicator },
})
export default class AppointmentCalendar extends Vue {
    @Prop({ default: 'appointment' }) eventType!: 'appointment' | 'task';
    @Prop() scheduledDay: string;
    @Prop() scheduledStartTime: string;
    @Prop() duration: DurationComboDto;
    @Prop() assignees: AssigneeComboDto[];
    @Prop() excludeEventId: string;
    /** Shown on the in-calendar preview block; falls back to i18n `calendar_selection_preview`. */
    @Prop({ default: '' }) selectionEventLabel!: string;
    /** Hex from selected action (`type_id.color`). Selected slot uses solid fill (no translucency). */
    @Prop({ default: '' }) selectionActionTypeColor!: string;
    @Prop({ default: false}) recurrentEvent!: boolean;
    @Prop({ default: false }) recurrenceFieldsShown!: boolean;

    // Tasks Props
    @Prop() dueAtTime: string;
    @Prop() allDay: boolean;

    focus = '';
    nowTime: string = dayjs().format('HH:mm');
    timer: number | null = null;
    /** One debounced pass for all @Watch side effects (fetch, overlap, scroll). */
    onChangeDebounceTimer: number | null = null;
    static readonly ON_CHANGE_DEBOUNCE_MS = 250;
    /** Bumped on each watch; stale `flushOnChangeEffects` runs bail after await. */
    onChangeFlushGeneration = 0;

    isReady = false;

    // Need this for the edit recurring appointment modal
    // Instead of checking the id (which is random), we have to check the scheduled start daytime
    originalScheduledDay = '';
    originalScheduledStartTime = '';
    scheduledStartChanged = false;

    beforeDestroy() {
        if (this.timer) clearInterval(this.timer);
        if (this.onChangeDebounceTimer != null) {
            clearTimeout(this.onChangeDebounceTimer);
            this.onChangeDebounceTimer = null;
        }
        appointmentsModule.unsetModalCalendarAppointments();
    }

    async mounted() {
        this.originalScheduledDay = this.scheduledDay;
        this.originalScheduledStartTime = this.scheduledStartTime;

        setTimeout(async () => {
            this.isReady = true;
            await this.$nextTick();
            this.updateTime();
            this.scrollCalendarToRelevantTime();

            if (this.scheduledDay && (this.scheduledStartTime || this.dueAtTime || this.isAllDayTask)) {
                await this.flushOnChangeEffects();
            }
        }, 350);
    }

    /**
     * Scrolls so the selected appointment time is in view (with context above it).
     * If there is no valid selected date/time, falls back to ~1h before current time.
     */
    scrollCalendarToRelevantTime() {
        const calendar = this.$refs.calendar as Record<string, any>;
        if (!calendar || typeof calendar.scrollToTime !== 'function') {
            return;
        }

        this.$nextTick(() => {
            let anchor = dayjs().subtract(1, 'hour');
            const day = this.scheduledDay;
            const time = this.eventType === 'task' ? this.dueAtTime : this.scheduledStartTime;
            if (day && time) {
                const selected = dayjs(`${day} ${time}`);
                if (selected.isValid()) {
                    anchor = selected.subtract(1, 'hour');
                    if (!anchor.isSame(selected, 'day')) {
                        anchor = selected.startOf('day');
                    }
                }
            }
            const scrollTime = anchor.format('HH:mm');
            calendar.scrollToTime(scrollTime);
        });
    }

    updateTime() {
        this.timer = setInterval(() => {
            this.nowTime = dayjs().format('HH:mm');
        }, 60000);
    }

    @Watch('scheduledDay')
    @Watch('scheduledStartTime')
    onScheduledStartTimeChange() {
        if (!this.scheduledStartChanged && this.scheduledDay && this.scheduledStartTime) {
            this.originalScheduledDay = this.scheduledDay;
            this.originalScheduledStartTime = this.scheduledStartTime;
            this.scheduledStartChanged = true;
        }
    }

    @Watch('scheduledDay')
    @Watch('scheduledStartTime')
    @Watch('dueAtTime')
    @Watch('duration')
    @Watch('assignees')
    @Watch('allDay')
    onChange() {
        if (!this.isReady) return;
        this.onChangeFlushGeneration++;
        if (this.onChangeDebounceTimer != null) {
            clearTimeout(this.onChangeDebounceTimer);
        }
        const genAtRun = this.onChangeFlushGeneration;
        this.onChangeDebounceTimer = window.setTimeout(() => {
            this.onChangeDebounceTimer = null;
            void this.flushOnChangeEffects(genAtRun);
        }, AppointmentCalendar.ON_CHANGE_DEBOUNCE_MS);
    }

    /** Same shape as the fetch branch in the former `onChange` (assignees must be a non-empty array). */
    calendarInputsReadyForFetch(): boolean {
        return !!(
            this.scheduledDay
            && Array.isArray(this.assignees)
            && this.assignees.length > 0
            && (this.isAllDayTask || this.calendarInputsReadyForOverlapCheck())
        );
    }

    get isAllDayTask(): boolean {
        return this.eventType === 'task' && this.allDay;
    }

    calendarInputsReadyForOverlapCheck(): boolean {
        if (this.isAllDayTask) return false;

        const hasTime = this.eventType === 'task' ? !!this.dueAtTime : (!!this.scheduledStartTime && !!this.duration);
        return !!(
            this.scheduledDay
            && hasTime
            && Array.isArray(this.assignees)
            && this.assignees.length > 0
        );
    }

    /**
     * Runs fetch + overlap check + scroll from current props.
     * @param staleGuardGen when set, aborts after awaits if a newer @Watch bump happened (in-flight fetch).
     */
    async flushOnChangeEffects(staleGuardGen?: number) {
        if (staleGuardGen != null && staleGuardGen !== this.onChangeFlushGeneration) {
            return;
        }

        if (this.calendarInputsReadyForFetch()) {
            await this.fetchAppointments();
            if (staleGuardGen != null && staleGuardGen !== this.onChangeFlushGeneration) {
                return;
            }

            if (this.calendarInputsReadyForOverlapCheck()) {
                const eventTime = this.eventType === 'task' ? this.dueAtTime : this.scheduledStartTime;
                const eventDuration = this.eventType === 'task' ? { text: '30', dur: 30 } : this.duration;

                await this.checkForEventsConflict(
                    this.scheduledDay,
                    eventTime!,
                    eventDuration,
                    this.assignees,
                    this.excludeEventId,
                );
            } else {
                appointmentsModule.setOverlappingEvents(null);
            }
        } else {
            appointmentsModule.unsetModalCalendarAppointments();
            appointmentsModule.setOverlappingEvents(null);
        }

        if (staleGuardGen != null && staleGuardGen !== this.onChangeFlushGeneration) {
            return;
        }
        await this.$nextTick();
        if (staleGuardGen != null && staleGuardGen !== this.onChangeFlushGeneration) {
            return;
        }
        this.scrollCalendarToRelevantTime();
    }

    async fetchAppointments() {
        const startOfDay = dayjs(this.scheduledDay).startOf('day').toISOString();
        const endOfDay = dayjs(this.scheduledDay).endOf('day').toISOString();

        const params = new URLSearchParams({ date_from: startOfDay, date_to: endOfDay });

        // TODO Stupid fix with filtering for undefind or null values because this is firing twice in Edit appointment modal, due to the assignee_id array being mutated from ['ID_UUID_ASSIGNEE'] -> [{id: 'ID_UUID_ASSIGNEE', name: 'ASSIGNEE_NAME'}] from the AssigneesMultiUserCombo???
        const assigneeIds = this.assignees.map(assignee => assignee.id).filter(id => id !== undefined && id !== null);

        assigneeIds.forEach(id => { params.append('assignee_id[]', id); });

        if (assigneeIds.length > 0) {
            await appointmentsModule.getModalCalendarAppointmentsAction(`?${params.toString()}`);
        }

        this.focus = this.scheduledDay;
        await this.$nextTick();
    }

    get draftSelectionEvent(): Record<string, any> | null {
        if (this.eventType === 'task' && this.allDay) return null;

        const day = this.scheduledDay;
        const time = this.eventType === 'task' ? this.dueAtTime : this.scheduledStartTime;
        const durMinutes = this.eventType === 'task' ? 30 : this.duration?.dur;

        if (!day || !time || durMinutes == null || durMinutes <= 0) {
            return null;
        }
        const start = dayjs(`${day} ${time}`);
        if (!start.isValid()) {
            return null;
        }
        const realEnd = start.add(durMinutes, 'minute');
        const startDate = start.toDate();
        const realEndDate = realEnd.toDate();
        let endDate = realEndDate;
        if (
            endDate.getDate() !== startDate.getDate()
            || endDate.getMonth() !== startDate.getMonth()
            || endDate.getFullYear() !== startDate.getFullYear()
        ) {
            endDate = new Date(startDate);
            endDate.setHours(23, 59, 59, 999);
        }
        const baseHex = (this.selectionActionTypeColor && this.selectionActionTypeColor.trim())
            ? this.selectionActionTypeColor.trim()
            : '#7e57c2';
        const barColor = baseHex;
        const label = (this.selectionEventLabel && this.selectionEventLabel.trim())
            || (this.$t('calendar_selection_preview') as string);
        const details =
            `${('0' + startDate.getHours()).slice(-2)}:${('0' + startDate.getMinutes()).slice(-2)}`
            + ' - '
            + `${('0' + realEndDate.getHours()).slice(-2)}:${('0' + realEndDate.getMinutes()).slice(-2)}`;
        return {
            name: label,
            start: startDate,
            end: endDate,
            color: barColor,
            textColor: getOptimalTextColor(barColor),
            timed: true,
            isDraftSelection: true,
            details,
            realStartDateTime: startDate,
            realEndDateTime: realEndDate,
        };
    }

   get events() {
        const excludeId = this.excludeEventId;

        const list = appointmentsModule.modalCalendarAppointments.appointments.filter(
            (event: Record<string, any>) => {
                if (this.isExcludedEvent(event, excludeId)) return false;

                if (this.recurrentEvent && this.originalScheduledDay && this.originalScheduledStartTime) {
                    const originalStart = dayjs(`${this.originalScheduledDay} ${this.originalScheduledStartTime}`);
                    if (event.start && dayjs(event.start).isSame(originalStart, 'minute')) return false;
                }

                return true;
            }) || [];

        const draft = this.draftSelectionEvent;
        return draft ? [...list, draft] : list;
    }

    isExcludedEvent(event: Record<string, any>, excludeId: string) {
        if (!excludeId) return false;
        const eventId = this.eventType === 'task' ? event.id : event.event_id;
        return eventId != null && String(eventId) === String(excludeId);
    }

    get modalCalendarAppointmentsLoader() {
        return appointmentsModule.modalCalendarAppointmentsLoader;
    }

    async checkForEventsConflict(scheduledDay: string, scheduledStartTime: string, duration: DurationComboDto, assignees: AssigneeComboDto[], excludeEventId: string) {
        if (!scheduledDay || !scheduledStartTime || !duration?.dur || assignees?.length === 0) {
            appointmentsModule.setOverlappingEvents(null);
            return;
        }

        const data = {
            user_timezone: dayjs.tz.guess(),
            assignee_ids: assignees.map(assignee => assignee.id),
            scheduled_start: dayjs(scheduledDay + ' ' + scheduledStartTime).toISOString(),
            duration: duration.dur,
            exclude_event_id: excludeEventId,
            type: this.eventType,
        };

        await appointmentsModule.getOverlappingEvents(data);
    }

    showStrikeThrough(event: Record<string, any>) {
        return event.status_id === 1 || event.appointment_status_id === 1 || event.task_status_id === 1;
    }

    /** Contrast for labels/icons against the event bar (`event.color`). */
    eventTextColor(event: Record<string, any>): string {
        const bg = event?.color;
        if (bg != null && String(bg).trim() !== '') {
            try {
                return getOptimalTextColor(String(bg).trim());
            } catch {
                /* hex-rgb may fail on unexpected values */
            }
        }
        if (event?.textColor) {
            return event.textColor;
        }
        return '#FFFFFF';
    }

    /** Single-line task chips (no installation context). */
    isCompactTaskEvent(event: Record<string, any>) {
        return event.isTask
            && (!event.installation_id || (event.installation_id && !event.task_category));
    }

    isRecurringAppointment(event: Record<string, any>) {
        return this.companyHasAdvancedPlan && !event.isTask && event.recurrence_id != null;
    }

    get companyHasAdvancedPlan() {
        return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
    }

    lineAssignee(event: Record<string, any>) {
        if (Array.isArray(event.assignees) && event.assignees.length > 0) {
            return event.assignees
                .map((a: unknown) => (typeof a === 'string' ? a : (a as { name?: string })?.name || ''))
                .filter(Boolean)
                .join(', ');
        }
        if (event.assignee_id) {
            return String(event.assignee_id);
        }
        return '';
    }

    lineType(event: Record<string, any>) {
        if (event.isTask) {
            return event.name ? String(event.name) : '';
        }
        const t = event.type_name || event.name;
        return t ? String(t) : '';
    }

    lineAddress(event: Record<string, any>) {
        const street = (event.address || '').toString().trim();
        const area = (event.area || '').toString().trim();
        if (street) {
            return street;
        }
        if (area) {
            return area;
        }
        if (event.isTask) {
            return '';
        }
        if (event.is_business) {
            const entity = (event.entity_name || '').toString().trim();
            const client = (event.client_name || '').toString().trim();
            if (entity && client) {
                return `${entity} (${client})`;
            }
            return entity || client;
        }
        return (event.client_name || '').toString().trim();
    }

    priorityIcon(name: string) {
        return priorityIcon(name);
    }

}
